<script setup lang="ts">
import { storeToRefs } from 'pinia';
import ProblemDetailsView from 'shared/problems/components/ProblemDetailsView.vue';
import type { ProblemDetails } from 'shared/problems/domain/problemDetails';
import type { ProductionOrder, ProductionOrderRecipe } from 'shared/productionOrders/domain/productionOrder';
import { useProductionOrderStore } from 'shared/productionOrders/stores/productionOrderStore';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Card, InputText, Message, Button, Textarea, Select } from 'primevue';
import { ProductionOrderState } from 'shared/productionOrders/domain/productionOrderState';
import EditProductionOrderRecipeDialog from './components/EditProductionOrderRecipeDialog.vue';
import MainLayout from '@/common/components/MainLayout.vue';
import { useCreateProductionOrderApi } from './api/createProductionOrderApi';
import type { ProductionOrderRequest } from './api/common/productionOrderRequest';
import { useActionFailedAlert } from 'shared/problems/composables/actionFailedAlert';
import { useUpdateProductionOrderApi, type UpdateProductionOrderRequest } from './api/updateProductionOrderApi';

const problems = ref<ProblemDetails>();
const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const store = useProductionOrderStore();
const editRecipeDialog = useTemplateRef('edit-recipe-dialog');

const createApi = useCreateProductionOrderApi();
const updateApi = useUpdateProductionOrderApi();

const route = useRoute();
const router = useRouter();

const current = ref<ProductionOrder | null>(null);

const actionFailedAlert = useActionFailedAlert();

const name = ref<string>('');
const nameError = computed(() => {
  if (name.value === '') {
    return translations.value.error.nameIsRequired;
  }

  return undefined;
});
const nameChanged = computed(() => {
  if (!current.value) return false;
  return current.value.name !== name.value;
});

const state = ref<ProductionOrderState | null>(null);
const stateChanged = computed(() => {
  if (!current.value) return false;
  return current.value.state !== state.value;
});
const stateList = [
  ProductionOrderState.Deleted,
  ProductionOrderState.Planned,
  ProductionOrderState.Released,
  ProductionOrderState.Reserved,
  ProductionOrderState.Preparing,
  ProductionOrderState.Prepared,
  ProductionOrderState.Delivering,
  ProductionOrderState.Completed,
  ProductionOrderState.Registered,
  ProductionOrderState.PreparingPaused,
  ProductionOrderState.DeliveringPaused,
];
const stateOptions = stateList.map<StateOptions>((t) => {
  return {
    value: t,
    name: translations.value.enums.productionOrderState(t),
  };
});

const resource = ref<string | null>(null);
const resouceChanged = computed(() => {
  if (!current.value) return false;
  return current.value.resource !== resource.value;
});

const priority = ref<string | null>(null);
const priorityChanged = computed(() => {
  if (!current.value) return false;
  return current.value.priority !== priority.value;
});

const productionInstruction = ref<string | null>(null);
const productionInstructionChanged = computed(() => {
  if (!current.value) return false;
  return current.value.productionInstruction !== productionInstruction.value;
});

const vehicleInstruction = ref<string | null>(null);
const vehicleInstructionChanged = computed(() => {
  if (!current.value) return false;
  return current.value.vehicleInstruction !== vehicleInstruction.value;
});

const recipes = ref<ProductionOrderRecipe[]>([]);
const recipesChanged = computed(() => {
  if (!current.value) return false;
  return JSON.stringify(current.value.recipes) !== JSON.stringify(recipes.value);
});

interface StateOptions {
  name: string;
  value: ProductionOrderState;
}

onMounted(async () => {
  const productOrderId = route.params.id as string | undefined;
  if (!productOrderId) {
    return;
  }

  const productionorder = store.getById(productOrderId);
  if (!productionorder) {
    return;
  }

  current.value = productionorder;

  name.value = productionorder.name;
  resource.value = productionorder.resource;
  priority.value = productionorder.priority?.toString() ?? null;
  state.value = productionorder.state;
  productionInstruction.value = productionorder.productionInstruction;
  vehicleInstruction.value = productionorder.vehicleInstruction;
  recipes.value = productionorder.recipes;
});


function showAddRecipe() {
  editRecipeDialog.value?.show({ recipe: null });
}


function showEditRecipe(recipe: ProductionOrderRecipe) {
  editRecipeDialog.value?.show({
    recipe: recipe
  });
}

function addRecipe(recipe: ProductionOrderRecipe) {
  const existingIndex = recipes.value.findIndex(r => r.id === recipe.id);
  if (existingIndex >= 0) {
    recipes.value[existingIndex] = recipe;
  } else {
    recipes.value.push(recipe);
  }
}

function deleteRecipe(recipe: ProductionOrderRecipe) {
  recipes.value = recipes.value.filter(r => r.id !== recipe.id);
}

const loading = ref(false);
async function save() {
  try {
    loading.value = true;
    const productionOrderId = current.value?.id;
    if (productionOrderId) {
      const productionOrder: UpdateProductionOrderRequest = {
        id: productionOrderId,
        name: name.value,
        resource: resource.value,
        priority: priority.value ? Number(priority.value) : null,
        state: state.value ?? ProductionOrderState.Planned,
        productionInstruction: productionInstruction.value,
        vehicleInstruction: vehicleInstruction.value,
        recipes: recipes.value,
        plannedStartUtc: null,
        plannedEndUtc: null,
        actualStartUtc: null,
        actualEndUtc: null,
        activeIntake: false,
        activeProduction: false,
      };

      const response = await updateApi.execute({ id: productionOrderId, entity: productionOrder });

      if (!response.ok) {
        actionFailedAlert.show(response.details);
        return;
      }

      name.value = response.productionOrder.name;
      resource.value = response.productionOrder.resource;
      priority.value = response.productionOrder.priority?.toString() ?? null;
      state.value = response.productionOrder.state;
      productionInstruction.value = response.productionOrder.productionInstruction;
      vehicleInstruction.value = response.productionOrder.vehicleInstruction;
      recipes.value = response.productionOrder.recipes;

      current.value = response.productionOrder;

    } else {
      const productionOrder: ProductionOrderRequest = {
        name: name.value,
        resource: resource.value,
        priority: priority.value ? Number(priority.value) : null,
        state: state.value ?? ProductionOrderState.Planned,
        productionInstruction: productionInstruction.value,
        vehicleInstruction: vehicleInstruction.value,
        recipes: recipes.value,
        plannedStartUtc: null,
        plannedEndUtc: null,
        actualStartUtc: null,
        actualEndUtc: null,
        activeIntake: false,
        activeProduction: false,
      };

      const response = await createApi.execute({ entity: productionOrder });

      if (!response.ok) {
        actionFailedAlert.show(response.details);
        return;
      }

      name.value = response.productionOrder.name;
      resource.value = response.productionOrder.resource;
      priority.value = response.productionOrder.priority?.toString() ?? null;
      state.value = response.productionOrder.state;
      productionInstruction.value = response.productionOrder.productionInstruction;
      vehicleInstruction.value = response.productionOrder.vehicleInstruction;
      recipes.value = response.productionOrder.recipes;

      current.value = response.productionOrder;
    }
  } finally {
    loading.value = false;
  }
}

function cancel() {
  router.push('/productionorders');
}
</script>

<template>
  <MainLayout>
    <Card class="productionorder-card">
      <template #title>
        <span>{{ current ? translations.productionOrders.editPage.editTitle :
          translations.productionOrders.editPage.createTitle }}</span>
      </template>
      <template #content>
        <div class="productionorder-grid">
          <ProblemDetailsView v-model="problems"></ProblemDetailsView>
          <div class="form container">
            <div class="field" :class="{ changed: nameChanged }">
              <label for="edit-productionorder-name">{{ translations.generic.name }}</label>
              <InputText autocomplete="off" autofocus :invalid="nameError !== undefined" id="edit-productionorder-name"
                v-model:model-value="name"></InputText>
              <Message v-if="nameError" severity="error" variant="simple">{{ nameError }}</Message>
            </div>

            <div class="field" :class="{ changed: stateChanged }">
              <label for="edit-productionorder-state">{{ translations.productionOrders.editPage.state }}</label>
              <Select id="edit-productionorder-state" name="state" :options="stateOptions" option-label="name"
                option-value="value" v-model="state">
              </Select>
            </div>

            <div class="field" :class="{ changed: resouceChanged }">
              <label for="edit-productionorder-resource">{{ translations.generic.resource }}</label>
              <InputText autocomplete="off" id="edit-productionorder-resource" v-model:model-value="resource">
              </InputText>
            </div>

            <div class="field" :class="{ changed: priorityChanged }">
              <label for="edit-productionorder-priority">{{ translations.generic.priority }}</label>
              <InputText autocomplete="off" id="edit-productionorder-priority" v-model:model-value="priority"
                type="number"></InputText>
            </div>

            <div class="field" :class="{ changed: productionInstructionChanged }">
              <label for="edit-productionorder-productioninstruction">{{
                translations.productionOrders.editPage.productionInstruction }}</label>
              <Textarea autocomplete="off" id="edit-productionorder-productioninstruction"
                v-model:model-value="productionInstruction"></Textarea>
            </div>

            <div class="field" :class="{ changed: vehicleInstructionChanged }">
              <label for="edit-productionorder-vehicleInstruction">{{
                translations.productionOrders.editPage.vehicleInstruction
                }}</label>
              <Textarea autocomplete="off" id="edit-productionorder-vehicleinstruction"
                v-model:model-value="vehicleInstruction"></Textarea>
            </div>

            <div class="field mt-2" :class="{ changed: recipesChanged }">
              <div class="flex">
                <label class="flex-1" for="edit-productionorder-recipes">{{
                  translations.productionOrders.editPage.recipesTitle }}</label>
                <Button type="button" class="flex-none" @click="showAddRecipe"
                  :label="translations.productionOrders.editPage.addRecipeButton"></Button>
              </div>
              <ul class="mt-2">
                <li v-for="recipe in recipes" :key="recipe.id" class="border-t-1 py-1">
                  <div class="flex items-center gap-2">
                    <span class="flex-1">{{ recipe.name }}</span>
                    <Button type="button" class="flex-none" @click="showEditRecipe(recipe)"
                      icon="pi pi-pencil"></Button>
                    <Button type="button" class="flex-none p-button-danger" @click="deleteRecipe(recipe)"
                      icon="pi pi-trash"></Button>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <div class="actions">
          <Button type="button" @click="save" :label="translations.generic.save"></Button>
          <Button type="button" class="ms-2" @click="cancel" :label="translations.generic.cancel"></Button>
        </div>
      </template>
    </Card>

    <EditProductionOrderRecipeDialog ref="edit-recipe-dialog" @confirm="addRecipe">
    </EditProductionOrderRecipeDialog>
  </MainLayout>
</template>
